// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import type { ReactNode } from "react";
import type { OpenProjectPayrollValueCorrectionFieldKey } from "../../types/openproject";

export type ValueCorrectionFieldKey =
  OpenProjectPayrollValueCorrectionFieldKey;

export type ValueCorrectionSource = "timesheet" | "payslip" | "calculated";

export type ValueCorrectionFieldDefinition = {
  key: ValueCorrectionFieldKey;
  metric: string;
  source: ValueCorrectionSource;
  sourceLabel: string;
  comparisonKey:
    | "overtime"
    | "vacation"
    | "remainingVacation"
    | "doctor"
    | "familyCare"
    | "illness"
    | "publicHoliday"
    | "paidLeave";
  comparisonSide: "timesheetValue" | "payslipValue" | null;
};

export const VALUE_CORRECTION_FIELDS = [
  {
    key: "timesheet.total",
    metric: "Timesheet total",
    source: "timesheet",
    sourceLabel: "Timesheet",
    comparisonKey: "overtime",
    comparisonSide: "timesheetValue",
  },
  {
    key: "payslip.paid",
    metric: "Payslip paid hours",
    source: "payslip",
    sourceLabel: "Payslip",
    comparisonKey: "overtime",
    comparisonSide: "payslipValue",
  },
  {
    key: "overtime.calculated",
    metric: "Calculated overtime",
    source: "calculated",
    sourceLabel: "Calculated",
    comparisonKey: "overtime",
    comparisonSide: null,
  },
  {
    key: "vacation.timesheet",
    metric: "Vacation",
    source: "timesheet",
    sourceLabel: "Timesheet",
    comparisonKey: "vacation",
    comparisonSide: "timesheetValue",
  },
  {
    key: "vacation.payslip",
    metric: "Vacation",
    source: "payslip",
    sourceLabel: "Payslip",
    comparisonKey: "vacation",
    comparisonSide: "payslipValue",
  },
  {
    key: "vacation_remaining.calculated",
    metric: "Vacation remaining",
    source: "calculated",
    sourceLabel: "Calculated",
    comparisonKey: "remainingVacation",
    comparisonSide: "timesheetValue",
  },
  {
    key: "vacation_remaining.payslip",
    metric: "Vacation remaining",
    source: "payslip",
    sourceLabel: "Payslip",
    comparisonKey: "remainingVacation",
    comparisonSide: "payslipValue",
  },
  {
    key: "doctor.timesheet",
    metric: "Doctor",
    source: "timesheet",
    sourceLabel: "Timesheet",
    comparisonKey: "doctor",
    comparisonSide: "timesheetValue",
  },
  {
    key: "doctor.payslip",
    metric: "Doctor",
    source: "payslip",
    sourceLabel: "Payslip",
    comparisonKey: "doctor",
    comparisonSide: "payslipValue",
  },
  {
    key: "family_care.timesheet",
    metric: "Family care",
    source: "timesheet",
    sourceLabel: "Timesheet",
    comparisonKey: "familyCare",
    comparisonSide: "timesheetValue",
  },
  {
    key: "family_care.payslip",
    metric: "Family care",
    source: "payslip",
    sourceLabel: "Payslip",
    comparisonKey: "familyCare",
    comparisonSide: "payslipValue",
  },
  {
    key: "illness.timesheet",
    metric: "Illness",
    source: "timesheet",
    sourceLabel: "Timesheet",
    comparisonKey: "illness",
    comparisonSide: "timesheetValue",
  },
  {
    key: "illness.payslip",
    metric: "Illness",
    source: "payslip",
    sourceLabel: "Payslip",
    comparisonKey: "illness",
    comparisonSide: "payslipValue",
  },
  {
    key: "public_holiday.timesheet",
    metric: "Public holiday",
    source: "timesheet",
    sourceLabel: "Timesheet",
    comparisonKey: "publicHoliday",
    comparisonSide: "timesheetValue",
  },
  {
    key: "public_holiday.payslip",
    metric: "Public holiday",
    source: "payslip",
    sourceLabel: "Payslip",
    comparisonKey: "publicHoliday",
    comparisonSide: "payslipValue",
  },
  {
    key: "paid_leave.timesheet",
    metric: "Paid leave",
    source: "timesheet",
    sourceLabel: "Timesheet",
    comparisonKey: "paidLeave",
    comparisonSide: "timesheetValue",
  },
  {
    key: "paid_leave.payslip",
    metric: "Paid leave",
    source: "payslip",
    sourceLabel: "Payslip",
    comparisonKey: "paidLeave",
    comparisonSide: "payslipValue",
  },
] as const satisfies readonly ValueCorrectionFieldDefinition[];

const fieldByKey = new Map(
  VALUE_CORRECTION_FIELDS.map((field) => [field.key, field]),
);

export const valueCorrectionField = (key: ValueCorrectionFieldKey) =>
  fieldByKey.get(key)!;

type CorrectionFieldState = {
  corrected?: boolean;
  reviewRequired?: boolean;
  sourceReviewState?: "not_corrected" | "reviewed" | "review_required";
};

export type ValueCorrectionSummaryRow = {
  timesheetHours?: number | null;
  payslipPaidHours?: number | null;
  overtimeDeltaHours?: number | null;
  vacationHours?: number | null;
  calculatedRemainingVacationHours?: number | null;
  payslipRemainingVacationHours?: number | null;
  doctorHours?: number | null;
  familyCareHours?: number | null;
  illnessHours?: number | null;
  publicHolidayHours?: number | null;
  paidLeaveHours?: number | null;
  effectiveValues?: Partial<
    Record<ValueCorrectionFieldKey, number | null>
  >;
  correctionMetadata?: {
    correctedFieldKeys?: ValueCorrectionFieldKey[];
    fields?: Partial<Record<ValueCorrectionFieldKey, CorrectionFieldState>>;
  };
  crossChecks?: Partial<
    Record<
      ValueCorrectionFieldDefinition["comparisonKey"],
      {
        timesheetValue?: number | null;
        payslipValue?: number | null;
      }
    >
  >;
};

const hasOwnValue = (
  values: ValueCorrectionSummaryRow["effectiveValues"],
  key: ValueCorrectionFieldKey,
) => Boolean(values && Object.prototype.hasOwnProperty.call(values, key));

const hasOwnRowValue = (row: ValueCorrectionSummaryRow, key: keyof ValueCorrectionSummaryRow) =>
  Object.prototype.hasOwnProperty.call(row, key);

export const valueCorrectionHours = (
  row: ValueCorrectionSummaryRow,
  key: ValueCorrectionFieldKey,
) => {
  if (hasOwnValue(row.effectiveValues, key))
    return row.effectiveValues?.[key] ?? null;

  if (key === "timesheet.total" && hasOwnRowValue(row, "timesheetHours"))
    return row.timesheetHours ?? null;
  if (key === "payslip.paid" && hasOwnRowValue(row, "payslipPaidHours"))
    return row.payslipPaidHours ?? null;

  const field = valueCorrectionField(key);
  if (field.comparisonSide) {
    const comparisonValue =
      row.crossChecks?.[field.comparisonKey]?.[field.comparisonSide];
    if (typeof comparisonValue === "number" || comparisonValue === null)
      return comparisonValue;
  }

  switch (key) {
    case "timesheet.total":
      return row.timesheetHours ?? null;
    case "payslip.paid":
      return row.payslipPaidHours ?? null;
    case "overtime.calculated":
      return row.overtimeDeltaHours ?? null;
    case "vacation.timesheet":
      return row.vacationHours ?? null;
    case "vacation_remaining.calculated":
      return row.calculatedRemainingVacationHours ?? null;
    case "vacation_remaining.payslip":
      return row.payslipRemainingVacationHours ?? null;
    case "doctor.timesheet":
      return row.doctorHours ?? null;
    case "family_care.timesheet":
      return row.familyCareHours ?? null;
    case "illness.timesheet":
      return row.illnessHours ?? null;
    case "public_holiday.timesheet":
      return row.publicHolidayHours ?? null;
    case "paid_leave.timesheet":
      return row.paidLeaveHours ?? null;
    default:
      return null;
  }
};

export const valueCorrectionStatus = (
  row: ValueCorrectionSummaryRow,
  key: ValueCorrectionFieldKey,
) => {
  const state = row.correctionMetadata?.fields?.[key];
  return {
    corrected:
      state?.corrected ??
      row.correctionMetadata?.correctedFieldKeys?.includes(key) ??
      false,
    reviewRequired:
      state?.reviewRequired ?? state?.sourceReviewState === "review_required",
  };
};

type ValueCorrectionButtonProps = {
  fieldKey: ValueCorrectionFieldKey;
  row: ValueCorrectionSummaryRow;
  children: ReactNode;
  onOpen: (
    fieldKey: ValueCorrectionFieldKey,
    trigger: HTMLButtonElement,
  ) => void;
};

export const ValueCorrectionButton = ({
  fieldKey,
  row,
  children,
  onOpen,
}: ValueCorrectionButtonProps) => {
  const field = valueCorrectionField(fieldKey);
  const status = valueCorrectionStatus(row, fieldKey);
  const stateLabel = status.reviewRequired
    ? ", correction needs source review"
    : status.corrected
      ? ", manually corrected"
      : "";

  return (
    <button
      type="button"
      className="value-correction-target"
      aria-label={`${field.metric}, ${field.sourceLabel}${stateLabel}. Open value details`}
      onClick={(event) => onOpen(fieldKey, event.currentTarget)}
    >
      <span>{children}</span>
      {status.corrected && (
        <span
          className={`value-correction-indicator${status.reviewRequired ? " value-correction-indicator--review" : ""}`}
          title={
            status.reviewRequired
              ? "Manual correction needs source review"
              : "Manually corrected"
          }
          aria-hidden="true"
        />
      )}
    </button>
  );
};

type ValueCorrectionComparisonTargetsProps = {
  row: ValueCorrectionSummaryRow;
  leftKey: ValueCorrectionFieldKey;
  rightKey: ValueCorrectionFieldKey;
  format: (valueHours: number | null) => ReactNode;
  onOpen: ValueCorrectionButtonProps["onOpen"];
};

export const ValueCorrectionComparisonTargets = ({
  row,
  leftKey,
  rightKey,
  format,
  onOpen,
}: ValueCorrectionComparisonTargetsProps) => (
  <span className="value-correction-comparison">
    <ValueCorrectionButton fieldKey={leftKey} row={row} onOpen={onOpen}>
      {format(valueCorrectionHours(row, leftKey))}
    </ValueCorrectionButton>
    <span className="value-correction-comparison__divider" aria-hidden="true">
      |
    </span>
    <ValueCorrectionButton fieldKey={rightKey} row={row} onOpen={onOpen}>
      {format(valueCorrectionHours(row, rightKey))}
    </ValueCorrectionButton>
  </span>
);
